import React from 'react';
import { ComponentNode } from '../types/template';
import { convertStylesToInline } from '../utils/styleUtils';
import { DataFetcher, fetchRawDataForRenderRequest } from '../utils/DataFetcher';
import ComponentRenderer from './ComponentRenderer';
import MobileNavToggle from './MobileNavToggle';
import {
    buildCategoryHref,
    buildEventHref,
    buildMerchantHref,
    buildPromotionHref,
} from '../utils/linkHelpers';

/** Finds the first node tagged with the given `builderPart`, wherever it
 * sits in the tree — stable regardless of which header layout variant
 * (inline/stacked/navCentre) nested it, unlike a fixed child index. */
const findPartNode = (node: ComponentNode, part: string): ComponentNode | undefined => {
    if ((node as any).builderPart === part) return node;
    for (const child of node.components ?? []) {
        const found = findPartNode(child, part);
        if (found) return found;
    }
    return undefined;
};

interface HeaderComponentProps {
    component: ComponentNode;
    data?: any;
    context?: {
        companyId?: string;
        domain?: string;
        store_slug?: string;
        slug_type?: string;
        category_slug?: string;
        promotion_slug?: string;
        out_link?: string;
        basePath?: string;
        pageSlug?: string;
        params?: Record<string, any>;
        searchParams?: Record<string, any>;
    };
}

type RequestConfig = {
    apiEndpoint: string;
    method: 'GET' | 'POST' | 'PUT' | 'DELETE';
    body?: Record<string, any>;
    params?: Record<string, any>;
};

const replaceTemplateValue = (value: any, variables: Record<string, string>): any => {
    if (typeof value === 'string') {
        return value.replace(/\{\{(.*?)\}\}/g, (_, rawKey: string) => {
            const key = rawKey.trim();
            return variables[key] ?? '';
        });
    }

    if (Array.isArray(value)) {
        return value.map((entry) => replaceTemplateValue(entry, variables));
    }

    if (value && typeof value === 'object') {
        const replaced: Record<string, any> = {};
        Object.entries(value).forEach(([key, nested]) => {
            replaced[key] = replaceTemplateValue(nested, variables);
        });
        return replaced;
    }

    return value;
};

const buildRequestConfig = (
    config: RequestConfig | undefined,
    variables: Record<string, string>
): RequestConfig | undefined => {
    if (!config?.apiEndpoint || !config?.method) {
        return undefined;
    }

    return {
        apiEndpoint: replaceTemplateValue(config.apiEndpoint, variables),
        method: config.method,
        body: config.body ? replaceTemplateValue(config.body, variables) : undefined,
        params: config.params ? replaceTemplateValue(config.params, variables) : undefined,
    };
};

const toCollection = (response: any): any[] => {
    const normalized = DataFetcher.normalizeCollectionResponse(response);
    return Array.isArray(normalized) ? normalized : [];
};

const firstUsableHref = (...values: unknown[]): string | undefined =>
    values.find(
        (value): value is string =>
            typeof value === 'string' && value.trim().length > 0 && value.trim() !== '#'
    );

const mapCategoryItem = (item: any, context?: HeaderComponentProps['context']) => ({
    ...item,
    category_name: item?.category_name || item?.name || item?.title || '',
    category_image:
        item?.category_image ||
        item?.category_logo ||
        item?.icon ||
        item?.image ||
        item?.logo ||
        '',
    category_url: item?.category_url || item?.url || buildCategoryHref(item, context) || '#',
});

const mapMerchantItem = (item: any, context?: HeaderComponentProps['context']) => {
    const merchantUrl = item?.merchant_url || item?.url || buildMerchantHref(item, context) || '#';

    return {
        ...item,
        merchant_name: item?.merchant_name || item?.name || item?.title || '',
        merchant_url: merchantUrl,
    };
};

const mapPromotionItem = (item: any, context?: HeaderComponentProps['context']) => {
    // These APIs can return url="#" or a generic /store/... URL. Build the
    // detail route from the actual promotion slug first, and only use an API
    // URL if it is usable and no slug-based route could be built.
    const slugHref = buildPromotionHref(item, context);
    return {
        ...item,
        promotion_name: item?.promotion_name || item?.name || item?.title || '',
        promotion_url:
            slugHref !== '#' ? slugHref : firstUsableHref(item?.promotion_url, item?.url) || '#',
    };
};

const mapEventItem = (item: any) => {
    // Ignore placeholder/generic API URLs when a slug can form the event route.
    const slugHref = buildEventHref(item);
    return {
        ...item,
        event_name: item?.event_name || item?.name || item?.title || '',
        event_url:
            slugHref !== '#' ? slugHref : firstUsableHref(item?.event_url, item?.url) || '#',
    };
};

const NAME_TO_URL_PLACEHOLDER: Record<string, string> = {
    category_name: 'category_url',
    merchant_name: 'merchant_url',
    product_name: 'product_url',
    promotion_name: 'promotion_url',
    event_name: 'event_url',
};

const decorateDropdownItemHrefs = (node: ComponentNode, insideDropdown: boolean): ComponentNode => {
    const isDropdown =
        insideDropdown || (node as any).hoverTarget === true || node.customClassName === 'cb-nav-dropdown';
    const nextNode: ComponentNode = {
        ...node,
        components: node.components?.map((child) => decorateDropdownItemHrefs(child, isDropdown)),
    };

    const urlPlaceholder = nextNode.placeholder && NAME_TO_URL_PLACEHOLDER[nextNode.placeholder];
    // Dropdown JSON may carry a stale href such as "#" or the API's generic
    // [url]. For event and promotion labels, always bind to the slug-based URL
    // prepared by mapEventItem/mapPromotionItem above.
    if (isDropdown && urlPlaceholder) {
        nextNode.href = `[${urlPlaceholder}]`;
    }

    return nextNode;
};

const decorateBlogLink = (
    node: ComponentNode,
    blogUrl: string | undefined,
    blogTitle: string | undefined,
): ComponentNode | null => {
    if ((node as any).isBlogLink) {
        if (!blogUrl || !blogTitle) return null;
        return { ...node, href: blogUrl, data: blogTitle };
    }
    if (!node.components) return node;
    const components = node.components
        .map((child) => decorateBlogLink(child, blogUrl, blogTitle))
        .filter((child): child is ComponentNode => child !== null);
    return { ...node, components };
};

const makeMobileNavNode = (node: ComponentNode): ComponentNode => ({
    ...node,
    data: typeof node.data === 'string' ? node.data.replace(/\s+▾$/, '') : node.data,
    components: node.components?.map(makeMobileNavNode),
});

const normalizeRoutePath = (path?: string): string => {
    if (!path) return '';
    const normalized = `/${path}`.replace(/\/+/g, '/').replace(/\/$/, '');
    return normalized || '/';
};

const getNavItemMatchScore = (
    link: ComponentNode,
    context: HeaderComponentProps['context'],
): number => {
    if (!context) return 0;
    if ((link as any).isBlogLink && context.pageSlug === 'blog_page') return 1000;

    const pagePathBySlug: Record<string, string> = {
        home_page: '/',
        category_page: '/category',
        category_specific_page: '/category',
        store_page: '/all-stores',
        store_specific_page: `/${context.store_slug || 'store'}`,
        all_products_page: '/all-products',
        products_page: '/products',
        products_specific_page: '/products',
        all_products_specific_page: '/all-products',
        events_page: '/events',
        event_specific_page: '/events',
        promotion_page: `/${context.promotion_slug || 'promotion'}`,
        promotion_specific_page: `/${context.promotion_slug || 'promotion'}`,
        blog_page: '/blog',
    };
    const href = link.href;
    if (typeof href !== 'string') return 0;
    const hrefPath = normalizeRoutePath(href.split(/[?#]/, 1)[0]);
    const currentPath = normalizeRoutePath(
        context.basePath || pagePathBySlug[context.pageSlug || ''],
    );
    if (!hrefPath || !currentPath) return 0;

    if (hrefPath === '/') return currentPath === '/' ? 1000 : 0;
    if (currentPath === hrefPath) return 1000 + hrefPath.length;
    if (currentPath.startsWith(`${hrefPath}/`)) return 500 + hrefPath.length;
    if (
        hrefPath.startsWith('/all-stores/') &&
        (currentPath === '/all-stores' || currentPath.startsWith('/all-stores/'))
    ) return 750;

    const storePath = normalizeRoutePath(context.store_slug);
    const promotionPath = normalizeRoutePath(context.promotion_slug);
    if (
        hrefPath.startsWith('/all-stores') &&
        storePath !== '/' &&
        currentPath.startsWith(storePath)
    ) return 100 + storePath.length;
    if (
        hrefPath === '/promotion' &&
        promotionPath !== '/' &&
        currentPath.startsWith(promotionPath)
    ) return 100 + promotionPath.length;
    return 0;
};

const applyRouteAwareNavStyles = (
    node: ComponentNode,
    context: HeaderComponentProps['context'],
): ComponentNode => {
    const navNode = findPartNode(node, 'nav');
    if (!navNode) return node;

    const navLinks: ComponentNode[] = [];
    const collectNavLinks = (current: ComponentNode) => {
        if ((current as any).hoverTarget) return;
        if (current.type === 'LINK') navLinks.push(current);
        current.components?.forEach(collectNavLinks);
    };
    collectNavLinks(navNode);
    const activeNavLink = navLinks
        .map((link) => ({ link, score: getNavItemMatchScore(link, context) }))
        .filter(({ score }) => score > 0)
        .sort((left, right) => right.score - left.score)[0]?.link;

    const activeTemplate = navLinks.find((link) => {
        const styles = link.styles ?? {};
        return styles.backgroundColor !== undefined || styles.borderBottom !== undefined;
    }) ?? (() => {
        const colorCounts = new Map<string, number>();
        navLinks.forEach((link) => {
            const color = link.styles?.color;
            if (typeof color === 'string') colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
        });
        return navLinks.find((link) => {
            const color = link.styles?.color;
            return typeof color === 'string' && colorCounts.get(color) === 1;
        });
    })();

    const activeStyle = (navNode as any).navActiveStyle;
    const activeBackground =
        typeof (navNode as any).navActiveBackground === 'string'
            ? (navNode as any).navActiveBackground
            : activeTemplate?.styles?.backgroundColor ??
                (typeof activeTemplate?.styles?.borderBottom === 'string'
                    ? activeTemplate.styles.borderBottom.replace(/^2px solid\s+/, '')
                    : undefined);
    const activeColor =
        typeof (navNode as any).navActiveColor === 'string'
            ? (navNode as any).navActiveColor
            : activeTemplate?.styles?.color;
    const commonColor =
        typeof (navNode as any).navColor === 'string'
            ? (navNode as any).navColor
            : navLinks
        .map((link) => link.styles?.color)
        .filter((color): color is string => typeof color === 'string')
        .sort((left, right) =>
            navLinks.filter((link) => link.styles?.color === right).length -
            navLinks.filter((link) => link.styles?.color === left).length
        )[0];

    const applyStyles = (current: ComponentNode, insideNav = false): ComponentNode => {
        const isNav = insideNav || (current as any).builderPart === 'nav';
        if (isNav && (current as any).hoverTarget) return current;
        const isNavLink = isNav && current.type === 'LINK';
        const active = isNavLink && current.id === activeNavLink?.id;
        let styles = current.styles;

        if (isNavLink && styles) {
            const {
                backgroundColor: _backgroundColor,
                borderBottom: _borderBottom,
                ...inactiveStyles
            } = styles;
            const activeStyles =
                activeStyle === 'underline'
                    ? {
                        color: activeBackground,
                        ...(activeBackground ? { borderBottom: `2px solid ${activeBackground}` } : {}),
                        borderRadius: '0',
                    }
                    : activeStyle === 'plain'
                        ? { color: activeBackground }
                        : {
                            ...(activeBackground ? { backgroundColor: activeBackground } : {}),
                            ...(activeColor ? { color: activeColor } : {}),
                        };
            styles = {
                ...inactiveStyles,
                ...(typeof commonColor === 'string' ? { color: commonColor } : {}),
                ...(active ? activeStyles : {}),
            };
        }

        return {
            ...current,
            ...(styles ? { styles } : {}),
            components: current.components?.map((child) => applyStyles(child, isNav)),
        };
    };

    return applyStyles(node);
};

const HeaderComponent = async ({ component, data, context }: HeaderComponentProps) => {
    const { styles, customClassName, components, headerApiConfig } = component;
    const inlineStyles = convertStylesToInline(styles);
    const templateVars = {
        company_id: context?.companyId || '',
        domain: context?.domain || '',
    };
    const categoryRequest = buildRequestConfig(
        headerApiConfig?.apiNavCategory as RequestConfig | undefined,
        templateVars
    );
    const merchantRequest = buildRequestConfig(
        headerApiConfig?.apiGetNavMerchants as RequestConfig | undefined,
        templateVars
    );
    const promotionRequest = buildRequestConfig(
        headerApiConfig?.apiGetAllPromotion as RequestConfig | undefined,
        templateVars
    );
    const eventRequest = buildRequestConfig(
        headerApiConfig?.apiGetEvents as RequestConfig | undefined,
        templateVars
    );
    // FooterComponent.tsx already fetches this the same way for its social
    // links; the Blog tab needs the same response for blog_url/blog_title.
    const companyDataRequest: RequestConfig = {
        apiEndpoint: '/company/get_company_data',
        method: 'POST',
        params: { domain: context?.domain || 'default' },
    };
    const [categoriesResult, merchantsResult, promotionsResult, eventsResult, companyDataResult] =
        await Promise.allSettled([
            categoryRequest
                ? fetchRawDataForRenderRequest(categoryRequest, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                })
                : Promise.resolve(null),
            merchantRequest
                ? fetchRawDataForRenderRequest(merchantRequest, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                })
                : Promise.resolve(null),
            promotionRequest
                ? fetchRawDataForRenderRequest(promotionRequest, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                })
                : Promise.resolve(null),
            eventRequest
                ? fetchRawDataForRenderRequest(eventRequest, {
                    companyId: context?.companyId,
                    domain: context?.domain,
                })
                : Promise.resolve(null),
            fetchRawDataForRenderRequest(companyDataRequest, {
                companyId: context?.companyId,
                domain: context?.domain,
            }),
        ]);
    const companyData =
        companyDataResult.status === 'fulfilled'
            ? (companyDataResult.value as any)?.data || (companyDataResult.value as any)
            : null;
    const headerCollections: Record<string, any[]> = {
        categories:
            categoriesResult.status === 'fulfilled'
                ? toCollection(categoriesResult.value).map((item) => mapCategoryItem(item, context))
                : [],
        merchants:
            merchantsResult.status === 'fulfilled'
                ? toCollection(merchantsResult.value).map((item) => mapMerchantItem(item, context))
                : [],
        promotions:
            promotionsResult.status === 'fulfilled'
                ? toCollection(promotionsResult.value).map((item) => mapPromotionItem(item, context))
                : [],
        events:
            eventsResult.status === 'fulfilled'
                ? toCollection(eventsResult.value).map(mapEventItem)
                : [],
        products: [],
    };

    const mergedData = {
        ...(data && typeof data === 'object' ? data : {}),
        ...headerCollections,
        products:
            headerCollections.products && headerCollections.products.length > 0
                ? headerCollections.products
                : [
                    {
                        product_name: 'Branded Products',
                        product_url: '/products',
                    },
                ],
    };

    const withDropdownHrefs = decorateDropdownItemHrefs(component, false);
    const decoratedComponent =
        decorateBlogLink(withDropdownHrefs, companyData?.blog_url, companyData?.blog_title) ?? withDropdownHrefs;
    const routeAwareComponent = applyRouteAwareNavStyles(decoratedComponent, context);

    const navNode = findPartNode(routeAwareComponent, 'nav');
    const searchNode = findPartNode(routeAwareComponent, 'search');

    return (
        <section style={inlineStyles} className={customClassName}>
            {routeAwareComponent.components?.map((child) => (
                <ComponentRenderer
                    key={child.id}
                    component={child}
                    data={mergedData}
                    context={context}
                />
            ))}
            {(navNode || searchNode) && (
                <MobileNavToggle
                    search={
                        searchNode && (
                            <ComponentRenderer
                                component={{ ...searchNode, id: `${searchNode.id}-mobile`, customClassName: 'cb-part-search cb-part-search-mobile' }}
                                data={mergedData}
                                context={context}
                            />
                        )
                    }
                    nav={
                        navNode && (
                            <ComponentRenderer
                                component={{ ...makeMobileNavNode(navNode), id: `${navNode.id}-mobile`, customClassName: 'cb-part-nav cb-part-nav-mobile' }}
                                data={mergedData}
                                context={context}
                            />
                        )
                    }
                />
            )}
        </section>
    );
};

export default HeaderComponent;
